import { MutableRefObject, createRef, useEffect, useRef } from 'react'
import { Box, Chip, Typography, useMediaQuery, useTheme } from '@mui/material'

import { useMap } from 'src/contexts/journey-map.context'
import { ImageCarousel, RichDescription } from 'src/components'
import { IAccount, IStep, Ilocation } from 'src/types'
import { LocationsWrapper, StepImage, StepItem, StepMedia, StepsList } from './journey.styled'

interface IProps {
  steps: IStep[]
  account: IAccount
  topRef?: MutableRefObject<HTMLSpanElement | null>
}

const EMPTY_RICH_TEXT = '<p><br></p>'

export const FairtradeJourneySteps = ({ steps, account, topRef }: IProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { setMapCenter, setScrollRef } = useMap()

  const stepRefs = useRef<Record<string, MutableRefObject<HTMLLIElement | null>>>({})
  const registeredStepIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    steps.forEach((step) => {
      if (!stepRefs.current[step.id]) {
        stepRefs.current[step.id] = createRef<HTMLLIElement>()
      }

      if (!registeredStepIds.current.has(step.id)) {
        registeredStepIds.current.add(step.id)
        setScrollRef(step.id, stepRefs.current[step.id])
      }
    })
  }, [setScrollRef, steps])

  const handleLocationClick = (location: Ilocation) => {
    if (!location.location_address || location.latitude === null || location.longitude === null) {
      return
    }

    setMapCenter(location.latitude / 10_000, location.longitude / 10_000, 14)

    if (topRef?.current) {
      if (isMobile) {
        window.scrollTo({
          top: topRef.current.offsetTop,
          behavior: 'smooth',
        })
      } else {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  const getCoverImage = (step: IStep) => {
    const mediaItem = step.mainMedia?.[0] ?? step.media?.[0]
    return (
      mediaItem?.link_large ??
      mediaItem?.link_medium ??
      mediaItem?.link_small ??
      step.image_medium ??
      step.image_small ??
      step.image
    )
  }

  const chipColor = account.primary_color ?? theme.palette.primary.main
  const getStepMedia = (step: IStep) => {
    if (step.mainMedia && step.mainMedia.length > 0) {
      return step.mainMedia
    }
    return []
  }

  return (
    <StepsList>
      {steps.map((step) => {
        const imageSrc = getCoverImage(step)
        const existingRef = stepRefs.current[step.id]
        const stepRef =
          existingRef ?? (createRef<HTMLLIElement>() as MutableRefObject<HTMLLIElement | null>)

        if (!existingRef) {
          stepRefs.current[step.id] = stepRef
        }
        const stepMedia = getStepMedia(step)

        return (
          <StepItem key={step.id} ref={stepRef}>
            <Typography variant="h4" fontWeight={600}>
              {step.name}
            </Typography>

            {step.description && step.description !== EMPTY_RICH_TEXT && (
              <RichDescription html={step.description} sx={{ fontSize: '14px', lineHeight: 1.4 }} />
            )}

            {stepMedia.length > 0 ? (
              <StepMedia>
                <ImageCarousel images={stepMedia} isStep />
              </StepMedia>
            ) : (
              imageSrc && (
                <StepMedia>
                  <StepImage src={imageSrc} alt={step.name} loading="lazy" />
                </StepMedia>
              )
            )}

            {step.locations?.length > 0 && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography sx={{ fontSize: '16px' }} color="text.secondary">
                  {step.locations.length > 1 ? 'Locations' : 'Location'}
                </Typography>
                <LocationsWrapper>
                  {step.locations.map((location) => {
                    const hasCoordinates =
                      location.latitude !== null &&
                      location.longitude !== null &&
                      location.latitude !== 0 &&
                      location.longitude !== 0

                    return (
                      <Chip
                        key={location.id}
                        label={location.location_name ?? 'Location'}
                        variant="outlined"
                        onClick={() => hasCoordinates && handleLocationClick(location)}
                        sx={{
                          borderColor: chipColor,
                          color: chipColor,
                          cursor: hasCoordinates ? 'pointer' : 'default',
                          opacity: hasCoordinates ? 1 : 0.6,
                        }}
                      />
                    )
                  })}
                </LocationsWrapper>
              </Box>
            )}
          </StepItem>
        )
      })}
    </StepsList>
  )
}
